import { Link } from 'react-router-dom';
import {
  Eye, CheckCircle2, Download, FileType, FileDown,
  Package, Share2, RotateCcw, PenTool
} from 'lucide-react';

const statusColors = {
  processing: 'bg-blue-950/50 text-blue-400 border-blue-900',
  review: 'bg-orange-950/50 text-primary border-orange-900',
  completed: 'bg-emerald-950/50 text-emerald-400 border-emerald-900',
  synced: 'bg-emerald-950/50 text-emerald-400 border-emerald-900',
  published: 'bg-emerald-950/50 text-emerald-400 border-emerald-900',
  created: 'bg-secondary/50 text-muted-foreground border-border',
  uploaded: 'bg-secondary/50 text-muted-foreground border-border',
  failed: 'bg-red-950/50 text-red-400 border-red-900',
};

export function JobHeader({
  job, jobId, approving, onApproveAll, onExport, onExportDxf, onExportPdf,
  onExportPackage, onOpenShareModal, retrying, failedPageCount, onRetryFailed,
}) {
  return (
    <div className="flex items-start justify-between mb-6" data-testid="job-header">
      <div>
        <Link to="/jobs" className="text-xs text-muted-foreground hover:text-muted-foreground font-mono mb-2 inline-block transition-colors">
          &larr; Back to Jobs
        </Link>
        <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground">
          {job.project_name || 'Untitled Project'}
        </h1>
        <div className="flex items-center gap-4 mt-1">
          <span className="font-mono text-xs text-muted-foreground">{job.facility_key}</span>
          <span className="font-mono text-xs text-muted-foreground">{job.revision}</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-medium font-mono uppercase tracking-wide border ${statusColors[job.status] || statusColors.created}`}>
            {job.status}
          </span>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap justify-end">
        <Link
          to={`/jobs/${jobId}/review`}
          data-testid="review-inspect-btn"
          className="flex items-center gap-2.5 rounded-sm bg-orange-600 hover:bg-orange-500 text-white font-heading font-bold uppercase tracking-wider text-xs h-10 px-5 transition-colors shadow-lg shadow-orange-600/20"
        >
          <PenTool className="w-4 h-4" />
          Open Drawing Editor
        </Link>
        <button
          onClick={onApproveAll}
          disabled={approving}
          data-testid="approve-all-btn"
          className="flex items-center gap-2 rounded-sm border border-emerald-800 bg-emerald-950/30 hover:bg-emerald-950/50 text-emerald-400 font-heading uppercase tracking-wider text-xs h-9 px-4 transition-colors disabled:opacity-50"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          {approving ? 'Approving...' : 'Auto-Approve 90%+'}
        </button>
        <button
          onClick={onExport}
          data-testid="export-json-btn"
          className="flex items-center gap-2 rounded-sm border border-border bg-transparent hover:bg-secondary text-muted-foreground font-heading uppercase tracking-wider text-xs h-9 px-4 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Export JSON
        </button>
        <button
          onClick={onExportDxf}
          data-testid="export-dxf-btn"
          className="flex items-center gap-2 rounded-sm border border-teal-800 bg-teal-950/30 hover:bg-teal-950/50 text-teal-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors"
        >
          <FileType className="w-3.5 h-3.5" />
          Export DXF
        </button>
        <button
          onClick={onExportPdf}
          data-testid="export-pdf-btn"
          className="flex items-center gap-2 rounded-sm border border-rose-800 bg-rose-950/30 hover:bg-rose-950/50 text-rose-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors"
        >
          <FileDown className="w-3.5 h-3.5" />
          Export PDF
        </button>
        <button
          onClick={onExportPackage}
          data-testid="export-package-btn"
          className="flex items-center gap-2 rounded-sm border border-violet-800 bg-violet-950/30 hover:bg-violet-950/50 text-violet-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors"
        >
          <Package className="w-3.5 h-3.5" />
          Digitization Package
        </button>
        <button
          onClick={onOpenShareModal}
          data-testid="share-portal-btn"
          className="flex items-center gap-2 rounded-sm border border-blue-800 bg-blue-950/30 hover:bg-blue-950/50 text-blue-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
          Client Portal
        </button>
        {failedPageCount > 0 && job.status !== 'processing' && (
          <button
            onClick={onRetryFailed}
            disabled={retrying}
            data-testid="retry-failed-btn"
            className="flex items-center gap-2 rounded-sm border border-amber-800 bg-amber-950/30 hover:bg-amber-950/50 text-amber-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying...' : `Retry ${failedPageCount} Failed`}
          </button>
        )}
      </div>
    </div>
  );
}
